import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { Icon } from "@tabler/icons-react"
import { type VariantProps, cva } from "class-variance-authority"

import { cn } from "@/lib/utils"

import { Spinner } from "./Spinner"

const buttonVariants = cva(
  cn(
    "group/button",
    "inline-flex items-center justify-center gap-2",
    "whitespace-nowrap",
    "rounded",
    "transition-colors",
    "focus-outline",
    "aria-disabled:pointer-events-none",
    "border border-solid",
  ),
  {
    variants: {
      variant: {
        primary: [
          "text-primary-foreground",
          "border-transparent",
          "bg-primary",

          "enabled:hover:bg-primary/50",
          "aria-expanded:bg-primary/50",
        ],

        // TODO: remove after rebrand
        secondary: [
          "text-secondary",
          "border-secondary",
          "bg-secondary-foreground",

          "enabled:hover:bg-secondary/15",
          "aria-expanded:bg-secondary/15",
        ],
        neutral: [
          "text-foreground",
          "border-neutral-foreground",
          "bg-white",

          "enabled:hover:bg-muted/80",
          "aria-expanded:bg-muted/80",
        ],
        danger: [
          "text-destructive",
          "border-destructive",
          "bg-destructive-foreground",

          "enabled:hover:bg-destructive/10",
          "aria-expanded:bg-destructive/10",
        ],
      },
      size: {
        small: "py-0.5 px-3 text-body2",
        medium: "py-1 px-4 text-body1",
        large: "py-2 px-5 text-h4",
      },
      iconPosition: {
        left: "",
        right: "flex-row-reverse",
      },
      iconOnly: {
        true: "",
        false: "",
      },
      loading: {
        true: "",
        false: "",
      },
      disabled: {
        false: "",
        true: "",
      },
      noBorder: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // loading
      {
        loading: true,
        className: "bg-primary/80 cursor-wait",
      },
      {
        variant: "primary",
        loading: true,
        className: "bg-primary/80",
      },
      {
        variant: "secondary",
        loading: true,
        className: "bg-secondary/10",
      },
      {
        variant: "neutral",
        loading: true,
        className: "bg-neutral-foreground/20",
      },
      {
        variant: "danger",
        loading: true,
        className: "bg-destructive/20",
      },
      // disabled
      {
        disabled: true,
        loading: false,
        className: "text-muted-foreground/60 cursor-not-allowed",
      },
      {
        variant: ["secondary", "neutral", "danger"],
        disabled: true,
        loading: false,
        className: "border-border",
      },
      {
        variant: "primary",
        disabled: true,
        loading: false,
        className: "bg-primary/40",
      },
      {
        variant: "neutral",
        disabled: true,
        loading: false,
        className: "bg-muted",
      },
      {
        variant: "secondary",
        disabled: true,
        loading: false,
        className: "bg-secondary/10",
      },
      {
        variant: "danger",
        disabled: true,
        loading: false,
        className: "bg-destructive/10",
      },
      // icon only
      {
        iconOnly: true,
        size: "small",
        className: "p-1.5",
      },
      {
        iconOnly: true,
        size: "medium",
        className: "p-2",
      },
      {
        iconOnly: true,
        size: "large",
        className: "p-3",
      },
      // no border
      {
        variant: ["secondary", "neutral", "danger"],
        noBorder: true,
        className: "border-transparent",
      },
      {
        variant: ["secondary", "neutral", "danger"],
        noBorder: true,
        loading: false,
        disabled: [false, true],
        className: "bg-transparent",
      },
    ],

    defaultVariants: {
      variant: "primary",
      size: "medium",
      iconPosition: "left",
      iconOnly: false,
      loading: false,
      disabled: false,
    },
  },
)

const contentVariants = cva("inline-flex items-center justify-center gap-2", {
  variants: {
    variant: {
      primary: "",
      secondary: "",
      neutral: "",
      danger: "",
    },
    loading: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      variant: "primary",
      loading: true,
      className: "text-primary-foreground/50",
    },
    {
      variant: "secondary",
      loading: true,
      className: "text-secondary/50",
    },
    {
      variant: "neutral",
      loading: true,
      className: "text-foreground/50",
    },
    {
      variant: "danger",
      loading: true,
      className: "text-destructive/50",
    },
  ],
})

export type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    icon?: Icon
    ref?: React.RefObject<HTMLButtonElement | null>
  }

function Button({
  className,
  variant,
  size,
  loading = false,
  disabled,
  icon,
  iconPosition,
  iconOnly,
  noBorder,
  children,
  ref,
  ...props
}: ButtonProps) {
  const IconComponent = icon
  const buttonIcon = IconComponent ? (
    loading ? (
      <Spinner size="1em" />
    ) : (
      <IconComponent className="shrink-0" />
    )
  ) : null

  return (
    <ButtonPrimitive
      ref={ref}
      className={cn(
        buttonVariants({
          variant,
          size,
          iconPosition,
          iconOnly,
          disabled,
          loading,
          noBorder,
          className,
        }),
      )}
      disabled={disabled || loading || false}
      nativeButton={props.render === undefined}
      {...props}
    >
      {loading && !IconComponent ? (
        <Spinner className="absolute h-fit" size="1em" />
      ) : (
        buttonIcon
      )}
      {!iconOnly && (
        <span
          className={cn(contentVariants({ variant, loading }), {
            invisible: loading && !IconComponent,
          })}
        >
          {children}
        </span>
      )}
    </ButtonPrimitive>
  )
}

Button.displayName = "Button"

export { Button, buttonVariants }
